import React, { useState } from 'react';
import { Share, ChevronDown, Check, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { AVAILABLE_LANGUAGES, LanguageCode } from '@/constants/menuUITranslations';

interface ShareLanguageDropdownProps {
  menuName: string;
  currentLanguage: LanguageCode;
  onLanguageChange: (lang: LanguageCode) => void;
  availableLanguages?: LanguageCode[];
  className?: string;
}

const ShareLanguageDropdown: React.FC<ShareLanguageDropdownProps> = ({
  menuName,
  currentLanguage,
  onLanguageChange,
  availableLanguages = ['fr'],
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const filteredLanguages = AVAILABLE_LANGUAGES.filter(lang => 
    availableLanguages.includes(lang.code)
  );

  const currentLang = AVAILABLE_LANGUAGES.find(l => l.code === currentLanguage);

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: `Menu de ${menuName}`,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href)
        .then(() => alert('Lien copié dans le presse-papier!'))
        .catch(err => console.error('Erreur de copie:', err));
    }
    setIsOpen(false);
  };

  const showLanguages = availableLanguages.length > 1;

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon"
          className={`bg-white/80 hover:bg-white text-gray-800 ${className}`}
          aria-label="Partager et changer de langue"
        >
          {showLanguages ? (
            <span className="text-lg">{currentLang?.flag || '🇫🇷'}</span>
          ) : (
            <Share className="h-5 w-5" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {/* Share option */}
        <DropdownMenuItem onClick={handleShare} className="cursor-pointer">
          <Share className="h-4 w-4 mr-2" />
          Partager
        </DropdownMenuItem>

        {/* Language options */}
        {showLanguages && (
          <>
            <DropdownMenuSeparator />
            <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground flex items-center gap-1">
              <Globe className="h-3 w-3" />
              Langue
            </div>
            {filteredLanguages.map((lang) => (
              <DropdownMenuItem
                key={lang.code}
                onClick={() => {
                  onLanguageChange(lang.code);
                  setIsOpen(false);
                }}
                className="cursor-pointer"
              >
                <span className="text-base mr-2">{lang.flag}</span>
                <span className="flex-1">{lang.nativeName}</span>
                {currentLanguage === lang.code && (
                  <Check className="h-4 w-4 text-primary" />
                )}
              </DropdownMenuItem>
            ))}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ShareLanguageDropdown;
